/**
 * @license Siftrock
 * (c) 2014-2018 Siftrock, Inc. http://siftrock.com
 * Unauthorized copying of this file, via any medium is strictly prohibited
 */

import { Component, OnInit } from '@angular/core';

import { BsModalService } from 'ngx-bootstrap/modal';

import { GlobalMessageService } from '../../services/global-message.service';
import { Constants, EmailType } from '../../global/constants';
import { AutomateJobService } from '../../services/automate-job.service';
import { ConfirmDialogComponent } from '../../global/confirm-dialog/confirm-dialog.component';


@Component({
  selector: 'app-route-track',
  templateUrl: './route-track.component.html'
})
export class RouteTrackComponent implements OnInit {

  loadingPage: boolean = false;
  showAllDetailClicked: boolean = false;
  jobs: any[] = [];

  constructor(private constants: Constants,
              private globalMessageService: GlobalMessageService,
              private modalService: BsModalService,
              private automateJobService: AutomateJobService) { }

  ngOnInit() {
    this.initView();
  }

  toggle(job: any) {
    job.enabled = !job.enabled;
    job.track_changes = true;
    let tempJob = JSON.parse(JSON.stringify(job));
    this.saveJob(tempJob, `Routing rule was ${job.enabled ? 'enabled' : 'disabled'}`);
  }

  showAllDetail() {
    this.showAllDetailClicked = true;
    this.jobs.forEach(job => {
      job.pretty.showDetail = true;
    });
  }

  hideAllDetail() {
    this.showAllDetailClicked = false;
    this.jobs.forEach(job => {
      job.pretty.showDetail = false;
    });
  }

  deleteJob(job: any) {
    let me = this;
    this.modalService.show(ConfirmDialogComponent, Object.assign({}, {
      initialState: {
        message: `WARNING: All history and associated data for this rule will be removed. Are you sure you want to delete?`,
        yesBtn: 'Delete Rule',
        confirm: function() {
          // first, remove the job from the array so the UI doesn't blow up when we drop meta data
          me.jobs.splice(me.jobs.findIndex(j=> { return j._id == job._id}), 1);

          job.enabled = false;
          job.deleted = true;

          job.tasks.forEach(task => {
            task.enabled = false;
            task.deleted = true;
            task.updated_at = new Date();
          });

          me.saveJob(job, 'Rule was deleted');
        }
      }
    }));
  }

  cloneJob(job: any) {
    let me = this;
    this.modalService.show(ConfirmDialogComponent, Object.assign({}, {
      initialState: {
        message: `Please confirm you\'d like to create a copy of the "${job.name}" rule. The new rule will be disabled until you enable it.`,
        yesBtn: 'Clone Rule',
        confirm: function() {
          let newJob = JSON.parse(JSON.stringify(job));
          delete newJob.pretty;
          newJob._id = 'new';
          newJob.name += ' (CLONE)';
          newJob.enabled = false;
          newJob.track_changes = false;

          // can't call this.saveJob because we need to get the result and push into the array
          me.automateJobService.saveJob(newJob).subscribe(
            (nj: any) => {
              nj = me.prettyJob(nj);
              me.jobs.push(nj);
              me.globalMessageService.message(`Rule was cloned to ${nj.name}`);
            },
            error => {
              // TODO
            }
          )
        }
      }
    }));
  }

  private saveJob(job: any, message: string) {
    delete job.pretty;
    this.automateJobService.saveJob(job).subscribe(
      (result: any) => {
        this.globalMessageService.message(message);
      },
      error => {
        // TODO
      }
    )
  }

  private initView() {
    this.loadingPage = true;
    this.automateJobService.getJobs('routetrack').subscribe(
      (result: any) => {
        this.jobs = result;
        this.jobs.forEach(j => {
          j = this.prettyJob(j);
        });
        this.loadingPage = false;
      },
      error => {

      }
    )
  }

  /**
   * Create meta data for displaying the list control
   */
  private prettyJob(job: any) {
    job.pretty = {
      invalid: false,
      showDetail: false,
      routeType: 'Not configured',
      route: [],
      leadOwner: {},
      track: false,
      issues: [],
      detailEmailTypes: [],
      detailSources: [],
      detailLanguages: [],
      detailList: 'No list specified',
      detailFields: 0
    };

    job.filters[this.getFilterIndexByType(job, 'email_type')].include.forEach(t => {
      job.pretty.detailEmailTypes.push(this.constants.emailTypes.find(et=> {return et.typeName == t}).display);
    });

    job.filters[this.getFilterIndexByType(job, 'source')].include.forEach(s => {
      job.pretty.detailSources.push(s);
    });

    if (job.pretty.detailSources.length == 0) {
      job.pretty.detailSources.push('All Sources');
    }

    job.filters[this.getFilterIndexByType(job, 'language')].include.forEach(l => {
      if (l == 'all') {
        job.pretty.detailLanguages.push('All Languages');
      }
      else if (l == 'other') {
        job.pretty.detailLanguages.push('Other');
      }
      else {
        job.pretty.detailLanguages.push(this.constants.languages.find(cl => { return cl.name == l}).display);
      }
    });

    let routeValid: boolean = true;
    let trackOnly: boolean = false;
    let trackValid: boolean = true;

    job.tasks.forEach(t => {
      if (!t.valid && t.invalid_reason) {
        job.pretty.issues.push(t.invalid_reason);
      }

      if (t.type == 'route') {
        if (!t.valid) {
          routeValid = false;
        }
        if (t.settings.type == 'static') {
          job.pretty.routeType = 'Specific People';
          if (t.settings.static.length > 0) {
            job.pretty.route = job.pretty.route.concat(t.settings.static);
          }
        }
        else if (t.settings.type == 'dynamic') {
          job.pretty.routeType = 'Dynamic Alias';
          if (t.settings.dynamic.length > 0) {
            job.pretty.route.push({email_address: `@${t.settings.dynamic}`});
            if (t.settings.static.length > 0) {
              job.pretty.route = job.pretty.route.concat(t.settings.static);
            }
          }
        }
        else if (t.settings.type == 'leadowner') {
          job.pretty.routeType = 'Lead Owner Lookup';
          if (t.settings.lead_owner.partner) {
            job.pretty.leadOwner = t.settings.lead_owner;
            job.pretty.route.push('placeholder'); // needed for UI consistency
          }
          if (t.settings.lead_owner.fallback && t.settings.lead_owner.fallback.length > 0) {
            job.pretty.leadOwner.fallback = t.settings.lead_owner.fallback;
          }
          if (t.settings.static.length > 0) {
            job.pretty.route = job.pretty.route.concat(t.settings.static);
          }

        }
        else if (t.settings.type == 'track') {
          job.pretty.routeType = 'Track Only';
          trackOnly = true;
        }
      }
      else if (t.type == 'track') {
        if (!t.valid) {
          trackValid = false;
        }
        if (trackValid) {
          job.pretty.track = true;
          job.pretty.detailList = t.settings.list.name || 'n/a';
          job.pretty.detailFields = t.settings.static_fields.length + t.settings.siftrock_fields.length;
        }
      }
    });

    if (job.pretty.issues.length == 0 && !routeValid && !trackValid) {
      job.pretty.issues = ['No route or track settings are configured for this rule'];
    }

    job.pretty.invalid = job.pretty.issues.length > 0 ? true : false;

    return job;
  }

  getFilterIndexByType(job: any, type: string) {
    return job.filters.findIndex(t => { return t.type == type});
  }
}



// WEBPACK FOOTER //
// ./client/app/config/route-track/route-track.component.ts